import { ComponentLoader, Dropdown } from 'cat-ecommerce-alloy';
import cx from 'classnames';
import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import resolvedEndpoints from '../../../../../constants/endpoints';
import InteractiveContent from '../../../../common/InteractiveContent';
import ProductSchematicsImage from './ProductSchematicsImage';
import ProductSchematicsPartsList from './ProductSchematicsPartsList';
import ProductSchematicsErrorFeedback from '../ProductSchematicsErrorFeedback';
import { replaceTokensInString } from '../../../../../utils';
import styles from './../styles.module.scss';
import { usePriceAndAvailability } from '../../../../../hooks';

const ProductSchematics = () => {
  const { data, error, invoke, status } = usePriceAndAvailability({
    namespace: 'schematics'
  });
  const ref = useRef();
  const [loading, setLoading] = useState(true);
  const catalogId = useSelector(s => s.common?.catalogId);
  const storeId = useSelector(s => s.common?.storeId);
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const uniqueId = useSelector(s => s.products?.current);
  const schematics = useSelector(s => s.schematics?.[serialNumber]?.[uniqueId]);
  const title = schematics.schematicTitle;
  const partsInformation = schematics?.parts;
  const diagramNames = schematics.diagrams.map(d => d.title);
  const [selected, setSelected] = useState(diagramNames[0]);
  const selectedSchematic = schematics.diagrams.find(d => d.title === selected);
  const diagram = selectedSchematic?.image;
  const parts = selectedSchematic?.parts;

  const { PRODUCT_SCHEMATICS_IMAGE } = resolvedEndpoints;
  const url = replaceTokensInString(
    PRODUCT_SCHEMATICS_IMAGE,
    catalogId,
    storeId,
    diagram.value
  );

  useEffect(() => {
    if (!isCatCorp) {
      const partsForPrice = parts.map(({ partNumber, quantity, uniqueId }) => ({
        partNumber,
        quantity,
        uniqueId
      }));

      setLoading(true);
      invoke({ parts: partsForPrice });
    }
  }, [invoke, isCatCorp, parts]);

  return (
    <>
      <div
        className={cx(
          'd-flex justify-content-between align-items-center display-3 pb-1',
          styles['diagram-header']
        )}
      >
        <h2 className="px-0 h3 mb-0">{title}</h2>
        <div className="d-none d-lg-block">
          {diagramNames.length > 1 ? (
            <Dropdown
              items={diagramNames}
              selected={selected}
              onChange={choice => setSelected(choice)}
              className={styles['diagram-dropdown']}
              hideLabel
            />
          ) : (
            <span className="d-block my-0 text-sans-serif text-md pt-2">
              {diagramNames[0]}
            </span>
          )}
        </div>
      </div>

      <ProductSchematicsErrorFeedback className="mt-3" preventScroll />

      <div className="d-block d-lg-none">
        <Dropdown
          items={diagramNames}
          selected={selected}
          onChange={choice => setSelected(choice)}
          className={cx(styles['diagram-dropdown'], 'pt-3', 'pt-lg-0')}
          hideLabel={true}
        />
      </div>

      <div className="row mt-3">
        <div
          className={`col-12 col-lg text-center position-sticky ${styles['diagram__image-container']}`}
        >
          <InteractiveContent
            className={`h-100 ${styles['product-schematic__interactive-content']} border p-2`}
            ref={ref}
          >
            {/* We need to force a rerender when changing equipments to get the onLoad event to work */}
            <ProductSchematicsImage
              onLoad={() => setLoading(false)}
              title={selectedSchematic?.title}
              url={url}
              type={diagram?.type}
              key={`${url}${serialNumber}`}
            />
          </InteractiveContent>
          {loading && <ComponentLoader />}
        </div>
        <div className="col-12 col-lg">
          <h3 className="h3 mt-3 mt-lg-0 display-4">{selected}</h3>
          <ProductSchematicsPartsList
            parts={parts}
            partsInformation={partsInformation}
            priceData={data}
            priceCallError={error}
            priceCallStatus={status}
            schematicsRef={ref}
            selected={selected}
          />
        </div>
      </div>
    </>
  );
};

export default ProductSchematics;
